import { Injectable, inject, signal, DestroyRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { FormErrorHandlerService, LanguageService } from '@ui-lib';
import {
  PreRegistrationActions,
  buildFormData,
} from './pre-registration-actions';
import {
  PreRegistrationStepKey,
  PreRegistrationSubmitResponse,
  PreRegistrationForm,
} from '@common/interfaces/pre-registration/pre-registration.interface';
import {
  mapStepPayload,
  mapFullPayload,
  mapApiErrorKeysToForm,
} from '../../../pages/pre-registration/pre-registration-config/pre-registration-payload.mapper';
import { PRE_REGISTRATION_STEPS } from '../../../pages/pre-registration/pre-registration-config/pre-registration-steps.config';
import { ROUTE_PATHS } from '@common/utils/route-paths.const';

@Injectable({ providedIn: 'root' })
export class PreRegistrationService {
  private readonly router = inject(Router);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly actions = inject(PreRegistrationActions);
  private readonly languageService = inject(LanguageService);

  /** Set once the request is submitted successfully; gates the confirmation page. */
  readonly referenceNumber = signal<string | null>(null);

  readonly isValidatingStep = signal(false);
  readonly isSubmitting = signal(false);

  /** Clears flow state when the shell (re)initializes. */
  reset(): void {
    this.referenceNumber.set(null);
    this.isValidatingStep.set(false);
    this.isSubmitting.set(false);
  }

  /**
   * Validates the current step's group client-side, then against the backend,
   * and only calls `onValid` when both pass.
   */
  validateStep(
    form: FormGroup,
    stepKey: PreRegistrationStepKey,
    groupName: string,
    destroyRef: DestroyRef,
    onValid: () => void
  ): void {
    const group = form.get(groupName) as FormGroup | null;
    if (!group) {
      onValid();
      return;
    }

    group.markAllAsTouched();
    this.formErrorHandler.validateDualLanguageFields(group);

    // Invalid fields surface inline (markAllAsTouched); no toast.
    if (group.invalid) {
      return;
    }

    this.isValidatingStep.set(true);
    this.actions
      .validateStep(mapStepPayload(stepKey, group.getRawValue()))
      .pipe(
        takeUntilDestroyed(destroyRef),
        finalize(() => this.isValidatingStep.set(false))
      )
      .subscribe({
        next: () => onValid(),
        error: (error: HttpErrorResponse) => this.handleApiErrors(error, group),
      });
  }

  /**
   * Validates the whole form, submits it as multipart form-data, then confirms.
   * `goToStep` lets the shell jump to the first step whose field the backend
   * rejected, so the inline error is visible (the review step is read-only).
   */
  submit(
    form: FormGroup,
    destroyRef: DestroyRef,
    goToStep?: (stepIndex: number) => void
  ): void {
    this.formErrorHandler.markAllControlsAsTouched(form);
    this.formErrorHandler.validateDualLanguageFields(form);

    // Invalid fields surface inline (markAllControlsAsTouched); no toast.
    if (form.invalid) {
      return;
    }

    this.isSubmitting.set(true);
    const payload = buildFormData(
      mapFullPayload(form.getRawValue() as PreRegistrationForm)
    );

    this.actions
      .submit(payload)
      .pipe(
        takeUntilDestroyed(destroyRef),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe({
        next: (res: PreRegistrationSubmitResponse) => {
          this.referenceNumber.set(res.data?.reference_number ?? '');
          this.navigateToConfirmation();
        },
        error: (error: HttpErrorResponse) => {
          const mappedErrors = this.handleApiErrors(error, form);
          const stepIndex = this.firstErroredStepIndex(mappedErrors);
          if (stepIndex >= 0) goToStep?.(stepIndex);
        },
      });
  }

  private navigateToConfirmation(): void {
    const lang = this.languageService.currentLanguage();
    this.router.navigate(
      ['/', lang, ROUTE_PATHS.PRE_REGISTRATION, ROUTE_PATHS.CONFIRMATION],
      { state: { byPassDeactivateGuard: true } }
    );
  }

  /**
   * Maps field-level validation errors onto their inputs (translating the
   * backend's key names to form control paths) so they surface inline — never as
   * a toast. General errors without a field map are left to the HTTP interceptor.
   * Returns the mapped error keys so callers can locate the offending step.
   */
  private handleApiErrors(
    error: HttpErrorResponse,
    group: FormGroup
  ): Record<string, string[]> {
    const mappedErrors = error.error?.errors
      ? mapApiErrorKeysToForm(error.error.errors)
      : {};

    if (Object.keys(mappedErrors).length) {
      this.formErrorHandler.setAllApiErrors(group, mappedErrors);
    }

    return mappedErrors;
  }

  /**
   * Returns the index of the earliest step whose form group appears in the
   * mapped error keys (keys are `<groupName>.…`), or -1 when none match.
   */
  private firstErroredStepIndex(
    mappedErrors: Record<string, string[]>
  ): number {
    const erroredGroups = new Set(
      Object.keys(mappedErrors).map(key => key.split('.')[0])
    );
    return PRE_REGISTRATION_STEPS.findIndex(
      step => !!step.formGroupName && erroredGroups.has(step.formGroupName)
    );
  }
}
